(function () {
  const CONFIG_ID = 'FreeShippingBar-config';
  const CART_PATH = /\/cart\/(add|change|update|clear)/i;
  const MOUNTS = [
    { selector: '#cart-notification-product', position: 'afterend', placement: 'notification' },
    { selector: '#main-cart-footer .js-contents', position: 'afterbegin', placement: 'cart' },
    { selector: '.cart-drawer__footer, .drawer__footer', position: 'afterbegin', placement: 'drawer' },
  ];

  let refreshing = false;
  let queued = false;

  function readConfig() {
    const node = document.getElementById(CONFIG_ID);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.warn('Free shipping bar: could not parse config', error);
      return null;
    }
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function wrapRemaining(template, money) {
    const amount = '<span class="fsb__amount">' + escapeHtml(money) + '</span>';
    return escapeHtml(template)
      .replace(/\[remaining\]/g, amount)
      .replace(/\{\{\s*remaining\s*\}\}/g, amount);
  }

  function formatMoney(cents, format) {
    const amount = (Math.max(0, Number(cents) || 0) / 100).toFixed(2);
    const [dollars, decimals] = amount.split('.');
    const withCommas = dollars.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const template = format || '${{amount}}';

    return template
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, withCommas)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, withCommas)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, `${withCommas},${decimals}`)
      .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/g, `${dollars.replace(/\B(?=(\d{3})+(?!\d))/g, "'")}.${decimals}`)
      .replace(/\{\{\s*amount\s*\}\}/g, `${withCommas}.${decimals}`);
  }

  function hasTag(tags, tag) {
    if (!tags || !tag) return false;
    const list = Array.isArray(tags)
      ? tags
      : String(tags)
          .split(',')
          .map((item) => item.trim());
    return list.some((item) => item.toLowerCase() === tag.toLowerCase());
  }

  function cacheKey(productId) {
    return `fsb-excluded:${productId}`;
  }

  function getCachedExclusion(productId) {
    try {
      const value = sessionStorage.getItem(cacheKey(productId));
      if (value === '1') return true;
      if (value === '0') return false;
    } catch (error) {
      /* private mode */
    }
    return null;
  }

  function setCachedExclusion(productId, excluded) {
    try {
      sessionStorage.setItem(cacheKey(productId), excluded ? '1' : '0');
    } catch (error) {
      /* private mode */
    }
  }

  function seedCache(config) {
    const map = config.productExcluded || {};
    Object.keys(map).forEach((id) => setCachedExclusion(id, Boolean(map[id])));
  }

  async function isExcluded(item, excludeTag, config) {
    const productId = String(item.product_id);
    if (config.productExcluded && Object.prototype.hasOwnProperty.call(config.productExcluded, productId)) {
      return Boolean(config.productExcluded[productId]);
    }

    const cached = getCachedExclusion(productId);
    if (cached !== null) return cached;

    if (item.tags && hasTag(item.tags, excludeTag)) {
      setCachedExclusion(productId, true);
      return true;
    }

    if (!item.handle) {
      setCachedExclusion(productId, false);
      return false;
    }

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const response = await fetch(`${root}products/${item.handle}.js`);
    if (!response.ok) {
      setCachedExclusion(productId, false);
      return false;
    }

    const product = await response.json();
    const excluded = hasTag(product.tags, excludeTag);
    setCachedExclusion(productId, excluded);
    return excluded;
  }

  function lineTotal(item) {
    if (typeof item.final_line_price === 'number') return item.final_line_price;
    if (typeof item.line_price === 'number') return item.line_price;
    return 0;
  }

  async function computeState(cart, config) {
    let eligible = 0;
    let hasExcluded = false;

    for (const item of cart.items || []) {
      const excluded = await isExcluded(item, config.excludeTag, config);
      if (excluded) {
        hasExcluded = true;
      } else {
        eligible += lineTotal(item);
      }
    }

    const remaining = Math.max(0, config.threshold - eligible);
    const progress = config.threshold > 0 ? Math.min(100, (eligible / config.threshold) * 100) : 0;

    let state = 'initial';
    if (eligible >= config.threshold && hasExcluded) state = 'achieved_mixed';
    else if (eligible >= config.threshold) state = 'achieved';
    else if ((cart.item_count || 0) > 0) state = 'progress';

    return { eligible, hasExcluded, remaining, progress, state };
  }

  function applyState(root, state, config) {
    const countryOk = !config.usaOnly || config.country === 'US';
    root.hidden = !countryOk;
    if (!countryOk) return;

    root.dataset.fsbState = state.state;
    root.setAttribute('data-show-progress', config.showProgress ? 'true' : 'false');

    const fill = root.querySelector('[data-fsb-fill]');
    if (fill) fill.style.width = `${state.progress}%`;

    const progressNode = root.querySelector('[data-fsb-state-message="progress"]');
    if (progressNode) {
      const template =
        progressNode.getAttribute('data-fsb-progress-template') || config.messages.progress || '';
      const remainingMoney = formatMoney(state.remaining, config.moneyFormat);
      progressNode.innerHTML = wrapRemaining(template, remainingMoney);
    }
  }

  function sourceBar() {
    return document.querySelector('[data-fsb][data-fsb-placement="header"]') || document.querySelector('[data-fsb]');
  }

  function ensureMounts() {
    const source = sourceBar();
    if (!source) return;

    MOUNTS.forEach((mount) => {
      const target = document.querySelector(mount.selector);
      if (!target) return;

      const host = mount.position === 'afterend' ? target.parentElement : target;
      if (host && host.querySelector(`[data-fsb-placement="${mount.placement}"]`)) return;

      const clone = source.cloneNode(true);
      clone.classList.remove('fsb--header', 'fsb--notification', 'fsb--cart', 'fsb--drawer');
      clone.classList.add(`fsb--${mount.placement}`);
      clone.dataset.fsbPlacement = mount.placement;
      clone.setAttribute('aria-live', 'off');

      if (mount.position === 'afterend') target.after(clone);
      else target.prepend(clone);
    });
  }

  async function refresh() {
    if (refreshing) {
      queued = true;
      return;
    }

    const config = readConfig();
    if (!config) return;

    refreshing = true;
    try {
      ensureMounts();
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const response = await fetch(`${root}cart.js`);
      if (!response.ok) return;
      const cart = await response.json();
      const state = await computeState(cart, config);
      document.querySelectorAll('[data-fsb]').forEach((node) => applyState(node, state, config));
    } catch (error) {
      console.warn('Free shipping bar: refresh failed', error);
    } finally {
      refreshing = false;
      if (queued) {
        queued = false;
        refresh();
      }
    }
  }

  function patchFetch() {
    if (window.fetch.__fsbPatched) return;
    const original = window.fetch;
    window.fetch = function patchedFetch() {
      const request = arguments[0];
      const url = typeof request === 'string' ? request : (request && request.url) || '';
      return original.apply(this, arguments).then((response) => {
        if (CART_PATH.test(url)) {
          response
            .clone()
            .json()
            .catch(() => null)
            .finally(() => refresh());
        }
        return response;
      });
    };
    window.fetch.__fsbPatched = true;
  }

  function subscribeToThemeCart() {
    try {
      if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
        subscribe(window.PUB_SUB_EVENTS.cartUpdate, () => refresh());
      }
    } catch (error) {
      /* pubsub not ready */
    }
    document.addEventListener('cart:updated', () => refresh());
    document.addEventListener('cart:change', () => refresh());
  }

  function init() {
    const config = readConfig();
    if (!config) return;
    seedCache(config);
    ensureMounts();

    const initialState = {
      eligible: config.eligible,
      hasExcluded: config.hasExcluded,
      remaining: Math.max(0, config.threshold - config.eligible),
      progress: config.threshold > 0 ? Math.min(100, (config.eligible / config.threshold) * 100) : 0,
      state: config.state,
    };
    document.querySelectorAll('[data-fsb]').forEach((node) => applyState(node, initialState, config));

    patchFetch();
    subscribeToThemeCart();
    document.addEventListener('shopify:section:load', refresh);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
